import React, { useState, useRef, useMemo, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { 
  Box, 
  Typography, 
  useTheme, 
  IconButton, 
  Tooltip as MuiTooltip,
  Menu,
  MenuItem,
  CircularProgress,
  styled
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { exportChartToPng } from '../../utils/chartUtils';
import { EChartsOption, GaugeSeriesOption } from 'echarts';
import debounce from 'lodash/debounce';

export interface EGaugeProps {
  /** Title for the gauge */
  title: string;
  /** Current value */
  value: number;
  /** Minimum value for the scale */
  min?: number;
  /** Maximum value for the scale */
  max?: number;
  /** Optimal range start */
  optimalStart?: number;
  /** Optimal range end */
  optimalEnd?: number;
  /** Units label */
  unit?: string;
  /** Gauge size */
  size?: number;
  /** Colors for different ranges */
  colors?: {
    belowOptimal?: string;
    optimal?: string;
    aboveOptimal?: string;
  };
  /** Last updated timestamp */
  lastUpdated?: Date | null;
  /** Function to refresh data */
  onRefresh?: () => void;
  /** Loading state */
  isLoading?: boolean;
}

const GaugeContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  margin: theme.spacing(2, 0),
  position: 'relative',
}));

const GaugeHeader = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const GaugeTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

const GaugeValue = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.text.primary,
}));

const GaugeOptimalRange = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}));

const LastUpdatedText = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255, 255, 255, 0.7)',
  borderRadius: theme.shape.borderRadius,
  zIndex: 5,
}));

/**
 * Water quality gauge component implemented with ECharts
 */
export const EWaterQualityGauge: React.FC<EGaugeProps> = ({
  title,
  value,
  min = 0,
  max = 100,
  optimalStart,
  optimalEnd,
  unit = '',
  size = 200,
  colors = {
    belowOptimal: '#f39c12',
    optimal: '#2ecc71',
    aboveOptimal: '#e74c3c',
  },
  lastUpdated,
  onRefresh,
  isLoading = false,
}) => {
  const theme = useTheme();
  const chartRef = useRef<ReactECharts>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);
  
  // Default optimal range if not provided
  const optStart = optimalStart !== undefined ? optimalStart : (max - min) * 0.25 + min;
  const optEnd = optimalEnd !== undefined ? optimalEnd : (max - min) * 0.75 + min;
  
  // Normalize value to percentage for the gauge
  const normalizedValue = Math.max(min, Math.min(max, value));
  
  // Determine color based on value
  let gaugeColor;
  if (normalizedValue < optStart) {
    gaugeColor = colors.belowOptimal;
  } else if (normalizedValue > optEnd) {
    gaugeColor = colors.aboveOptimal;
  } else {
    gaugeColor = colors.optimal;
  }

  // Create ECharts options
  const options = useMemo<EChartsOption>(() => {
    // Define axis ranges with colors based on optimal range
    const axisLine = {
      lineStyle: {
        width: 30,
        color: [
          [optStart / max, colors.belowOptimal || '#f39c12'],
          [optEnd / max, colors.optimal || '#2ecc71'],
          [1, colors.aboveOptimal || '#e74c3c'],
        ]
      }
    };

    return {
      series: [
        {
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          min,
          max,
          radius: '100%',
          axisLine,
          pointer: {
            icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
            length: '12%',
            width: 8,
            offsetCenter: [0, '-60%'],
            itemStyle: {
              color: gaugeColor
            }
          },
          axisTick: {
            length: 12,
            lineStyle: {
              color: 'auto',
              width: 2
            }
          },
          splitLine: {
            length: 20,
            lineStyle: {
              color: 'auto',
              width: 3
            }
          },
          axisLabel: {
            color: '#666',
            fontSize: 12,
            distance: 15,
            formatter: (value: number) => {
              return value.toString();
            }
          },
          title: {
            offsetCenter: [0, '-10%'],
            fontSize: 14,
            color: theme.palette.text.secondary,
            show: false
          },
          detail: {
            fontSize: 15,
            offsetCenter: [0, '30%'],
            valueAnimation: true,
            color: theme.palette.text.primary,
            formatter: (value: number) => {
              return `${value.toFixed(1)} ${unit}`;
            }
          },
          data: [{
            value: normalizedValue,
            name: 'Value'
          }]
        } as GaugeSeriesOption
      ]
    };
  }, [normalizedValue, min, max, optStart, optEnd, colors, unit, theme.palette.text]);

  // Create debounced refresh handler for performance
  const handleRefresh = useCallback(debounce(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, 300), [onRefresh]);

  const handleExportMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  }, []);
  
  const handleExportMenuClose = useCallback(() => {
    setExportMenuAnchor(null);
  }, []);
  
  const handleExportPng = useCallback(() => {
    handleExportMenuClose();
    if (chartRef.current) {
      const echartInstance = chartRef.current.getEchartsInstance();
      const dataURL = echartInstance.getDataURL();
      const link = document.createElement('a');
      link.download = `${title || 'water-quality-gauge'}.png`;
      link.href = dataURL;
      link.click();
    }
  }, [title, handleExportMenuClose]);

  const formattedLastUpdated = lastUpdated 
    ? `Updated: ${lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'})}`
    : '';
  
  return (
    <GaugeContainer>
      <GaugeHeader>
        <GaugeTitle variant="h6">{title}</GaugeTitle>
        <ControlsContainer>
          {onRefresh && (
            <MuiTooltip title="Refresh data">
              <IconButton size="small" onClick={handleRefresh} disabled={isLoading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          )}
          <MuiTooltip title="Export options">
            <IconButton size="small" onClick={handleExportMenuOpen}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
          <Menu
            anchorEl={exportMenuAnchor}
            open={exportMenuOpen}
            onClose={handleExportMenuClose}
          >
            <MenuItem onClick={handleExportPng}>Export as PNG</MenuItem>
            <MenuItem onClick={handleExportMenuClose}>Export as PDF</MenuItem>
          </Menu>
        </ControlsContainer>
      </GaugeHeader>
      
      <Box sx={{ width: size, height: size / 1.2, position: 'relative' }}>
        {isLoading && (
          <LoadingOverlay>
            <CircularProgress size={30} />
          </LoadingOverlay>
        )}
        <ReactECharts
          ref={chartRef}
          option={options}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
        />
      </Box>

      <GaugeOptimalRange>
        Optimal range: {optStart.toFixed(1)} - {optEnd.toFixed(1)} {unit}
      </GaugeOptimalRange>
      {lastUpdated && (
        <LastUpdatedText>
          {formattedLastUpdated}
        </LastUpdatedText>
      )}
    </GaugeContainer>
  );
};

// Export with React.memo for performance optimization
export default React.memo(EWaterQualityGauge);